import { loadEphemeralSession } from './ephemeral-session.js'
import { supabase } from './supabase.js'

/**
 * API クライアント。
 * - Supabase のセッション JWT を Authorization ヘッダに自動付与
 * - Supabase セッションが無く、暫定テナントセッションが localStorage にある場合は
 *   暫定 JWT を Authorization に乗せる
 * - エラーレスポンスは `{ success: false, error: { code, message, details? } }` 形式を想定
 */

const API_BASE = import.meta.env.VITE_API_URL ?? '/api'

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details: unknown

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
  }
}

export async function apiFetch<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  const supabaseToken = session?.access_token
  const ephemeral = supabaseToken ? null : loadEphemeralSession()
  const token = supabaseToken ?? ephemeral?.token

  const headers = new Headers(init.headers)
  if (token) headers.set('Authorization', `Bearer ${token}`)
  if (!headers.has('Content-Type') && init.body) headers.set('Content-Type', 'application/json')

  const res = await fetch(`${API_BASE}${path}`, { ...init, headers })

  let body: unknown
  try {
    body = await res.json()
  } catch {
    body = null
  }

  if (!res.ok) {
    const errBody = body as {
      success?: false
      error?: { code?: string; message?: string; details?: unknown }
    } | null
    throw new ApiError(
      res.status,
      errBody?.error?.code ?? 'UNKNOWN',
      errBody?.error?.message ?? `HTTP ${res.status}`,
      errBody?.error?.details,
    )
  }

  // 成功レスポンスは { success: true, data: ... } 形式
  const okBody = body as { success?: true; data?: T } | null
  return (okBody?.data ?? null) as T
}
